import { DataSource } from "@coeff/api";
import {
  AdminExtDataSource,
  DataSourceConnectionStatus,
  DataSourceType,
  DefaultApiGetDataSourcesRequest,
} from "@coeff/api-admin-ext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ColumnsType } from "antd/lib/table";
import moment from "moment";
import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

import { QueryCacheKey, SortColumn, SortDirection } from "../../../api";
import {
  AdminTableFooter,
  ImportTypeIcon,
  LoaderWithPerfTimings,
  DataSourceItemMenu,
  Typography,
  Tooltip,
  FlexRow,
  FlexColumn,
  SortDropdown,
  Button,
} from "../../../components";
import { AdminTable } from "../../../components/AdminTable";
import { StatusTag } from "../../../components/StatusTag";
import { KEY_LABEL, MULTIPLE_OAUTH_DATASOURCES, OAUTH_DATASOURCES } from "../../../constants";
import { getTimeZone, useApiContext } from "../../../utils";
import { TabName } from "../../WorkspaceAdmin/WorkspaceAdmin";
import { useTableFilters } from "../useTableFilters";

import { getSortIcon } from "./DataImports";
import { DomainDataSourceDetails } from "./DomainDataSourceDetails";
import { DomainDataSourcesFilterToolbar } from "./DomainDataSourcesFilterToolbar";

const Container = styled.div`
  height: 100%;
  width: 100%;

  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 12px;
  overflow: hidden;
  overflow-x: auto;
`;

export const DomainDataSources: React.FC = () => {
  const history = useHistory();

  const { adminExtApiClient } = useApiContext();

  const [search, setSearch] = useState("");

  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  const domain = searchParams.get("domain") ?? "all_domains";

  const [sortedColumn, setSortedColumn] = useState<SortColumn>({
    columnKey: "data_source_name",
    value: SortDirection.ascend,
  });

  const queryClient = useQueryClient();

  const [page, setPage] = React.useState(0);

  const [pageOffset, setPageOffset] = React.useState(0);

  const pageSize = 100;

  const setPageAndOffset = (page: number) => {
    setPage(page);
    setPageOffset(page * pageSize);
  };

  const { filters, setFilters } = useTableFilters<keyof DefaultApiGetDataSourcesRequest>([
    "data_source_type",
    "is_shared",
    "status",
    "user_id",
  ]);

  const onFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
    setPage(0);
    setPageOffset(0);
  };

  const [activeDataSourceId, setActiveDataSourceId] = React.useState<string>();

  const [editingName, setEditingName] = useState(false);

  const { data: dataSourcesResponse, isLoading } = useQuery(
    [
      QueryCacheKey.ADMIN_EXT_DATA_SOURCES,
      domain,
      pageSize,
      pageOffset,
      sortedColumn,
      search,
      filters,
    ],
    () =>
      adminExtApiClient.getDataSources({
        domain,
        page_size: pageSize,
        offset: pageOffset,
        sort_by: sortedColumn.columnKey,
        sort_order: sortedColumn.value,
        tz_name: getTimeZone(),
        search,
        // Filters
        is_shared: filters.is_shared as unknown as string,
        data_source_type: filters.data_source_type as unknown as string,
        status: filters.status as unknown as string,
        user_id: filters.user_id as unknown as string,
      }),
    {
      enabled: Boolean(domain),
    }
  );

  const dataSources = dataSourcesResponse?.data?.data_sources ?? [];

  const getReconnectStatusTooltip = (dataSourceType: DataSourceType, createdBy: string) => {
    if (MULTIPLE_OAUTH_DATASOURCES.includes(dataSourceType)) {
      return `This source needs to be reconnected by the owner ${createdBy}`;
    } else if (OAUTH_DATASOURCES.includes(dataSourceType)) {
      return `This source needs to be reconnected by the owner ${createdBy}`;
    } else {
      return "This source needs to be reconnected";
    }
  };

  const columns: ColumnsType<AdminExtDataSource> = [
    {
      title: <Typography noWrap>Source</Typography>,
      dataIndex: "data_source_name",
      key: "data_source_name",
      showSorterTooltip: false,
      width: 360,
      filterDropdown: ({ confirm }) => (
        <SortDropdown
          confirm={confirm}
          columnKey="data_source_name"
          setSortedColumn={setSortedColumn}
        />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "data_source_name"),
      render: (
        dataSourceName: AdminExtDataSource["data_source_name"],
        dataSource: AdminExtDataSource
      ) => {
        return (
          <FlexRow style={{ maxWidth: 360 }}>
            <ImportTypeIcon type={dataSource.data_source_type as DataSourceType} size="small" />

            <Typography noWrap title={dataSourceName} ellipsis>
              {dataSourceName}
            </Typography>
          </FlexRow>
        );
      },
    },
    {
      title: <Typography noWrap>{KEY_LABEL.data_source_sharing}</Typography>,
      dataIndex: "data_source_sharing",
      key: "data_source_sharing",
      showSorterTooltip: false,
      width: 180,
      render: (sharing: string) => {
        return (
          <Typography
            noWrap
            title={sharing}
            color={sharing === "None" || sharing === "Disabled" ? "textSecondary" : undefined}
          >
            {sharing}
          </Typography>
        );
      },
    },
    {
      title: <Typography noWrap>{KEY_LABEL.connection_status}</Typography>,
      dataIndex: "connection_status",
      key: "connection_status",
      showSorterTooltip: false,
      width: 160,
      render: (connectionStatus: AdminExtDataSource["connection_status"], dataSource) => {
        if (connectionStatus === "") {
          return null;
        }

        const statusLabel = connectionStatus === "ok" ? "OK" : "Reconnect";

        const connectionStatusDetails: DataSourceConnectionStatus | undefined =
          dataSource["connection_status_details"];

        let title: string | undefined;

        if (connectionStatusDetails) {
          const lastCheckedDt = moment(connectionStatusDetails.last_checked_dt);

          title = `Last used ${lastCheckedDt.format("MMM D, YYYY h:mma")}`;

          if (statusLabel === "Reconnect") {
            title = getReconnectStatusTooltip(
              dataSource["data_source_type"] as DataSourceType,
              dataSource["created_by"]
            );
          }
        }

        return (
          <Tooltip title={title}>
            <div style={{ display: "inline-block" }}>
              <StatusTag status={statusLabel === "OK" ? "ok" : "failed"} label={statusLabel} />
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: <Typography noWrap>{KEY_LABEL.created_by}</Typography>,
      dataIndex: "created_by",
      key: "created_by",
      showSorterTooltip: false,
      width: 200,
      filterDropdown: ({ confirm }) => (
        <SortDropdown confirm={confirm} columnKey="created_by" setSortedColumn={setSortedColumn} />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "created_by"),
      render: (_, dataSource) => {
        return (
          <div style={{ maxWidth: 200 }}>
            <Typography noWrap title={dataSource.created_by}>
              {dataSource.user_name}
            </Typography>

            {dataSource.created_dt ? (
              <Typography
                title={moment(dataSource.created_dt).format("MMM D, YYYY h:mma")}
                noWrap
                color="textSecondary"
                fontSize="13px"
              >
                {moment(dataSource.created_dt).fromNow()}
              </Typography>
            ) : null}
          </div>
        );
      },
    },
    {
      title: <Typography noWrap>{KEY_LABEL.num_data_imports}</Typography>,
      dataIndex: "num_data_imports",
      key: "num_data_imports",
      showSorterTooltip: false,
      width: 100,
      filterDropdown: ({ confirm }) => (
        <SortDropdown
          confirm={confirm}
          columnKey="num_data_imports"
          setSortedColumn={setSortedColumn}
        />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "num_data_imports"),
      render: (value, dataSource) => {
        return value === 0 ? (
          "-"
        ) : (
          <Button
            type="link"
            noPadding
            onClick={() => {
              const params = new URLSearchParams(location.search);

              params.append("data_source_id", JSON.stringify([dataSource.data_source_id]));

              history.push({
                pathname: location.pathname
                  .split("/")
                  .slice(0, -1)
                  .concat(TabName.imports)
                  .join("/"),
                search: params.toString(),
              });
            }}
          >
            {value}
          </Button>
        );
      },
    },
    {
      title: <Typography noWrap>{KEY_LABEL.num_writebacks}</Typography>,
      dataIndex: "num_writebacks",
      key: "num_writebacks",
      showSorterTooltip: false,
      width: 100,
      filterDropdown: ({ confirm }) => (
        <SortDropdown
          confirm={confirm}
          columnKey="num_writebacks"
          setSortedColumn={setSortedColumn}
        />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "num_writebacks"),
      render: value => {
        return value === 0 ? (
          "-"
        ) : (
          <Button type="link" noPadding>
            {value}
          </Button>
        );
      },
    },
    {
      title: "",
      showSorterTooltip: false,
      width: 40,
      render: (d: AdminExtDataSource) => {
        return (
          <DataSourceItemMenu
            noBackground
            noPadding
            dataSource={d as unknown as DataSource}
            onDeleteCb={() => {}}
            onRenameCb={() => {
              setActiveDataSourceId(d.data_source_id);
              setEditingName(true);
            }}
          />
        );
      },
    },
  ];

  return (
    <Container style={{ maxWidth: "1200px" }}>
      <DomainDataSourcesFilterToolbar
        domain={domain}
        search={search}
        onSearchChange={value => setSearch(value)}
        filters={filters}
        setFilters={onFilterChange}
      />

      <FlexColumn gap={0.5} style={{ overflow: "hidden" }}>
        <AdminTable
          onRow={dataSource => ({
            onClick: () => {
              queryClient.invalidateQueries([QueryCacheKey.ADMIN_EXT_DATA_SOURCE_DETAILS]);
              setActiveDataSourceId(dataSource.data_source_id);
            },
          })}
          columns={columns}
          dataSource={dataSources.map(d => ({ ...d, key: d.data_source_id }))}
          loading={false}
          locale={{
            emptyText: isLoading ? (
              <LoaderWithPerfTimings name="DomainDataSources.locale" />
            ) : (
              "No data sources found"
            ),
          }}
          pagination={false}
        />

        <AdminTableFooter
          page={page}
          pageSize={pageSize}
          onPageChange={page => setPageAndOffset(page)}
          data={dataSources.map(x => [x.data_source_name, true])}
          totalRowCount={dataSourcesResponse?.data?.total_records ?? 0}
        />
      </FlexColumn>

      <DomainDataSourceDetails
        editingName={editingName}
        setEditingName={setEditingName}
        dataSourceId={activeDataSourceId}
        setDataSourceId={setActiveDataSourceId}
      />
    </Container>
  );
};
